import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type DsHeroLayout = 'split' | 'centered' | 'brand';

const PROOF_LOGOS = ['Dupuis', 'Media Diffusion', 'MediaControl', 'Albin Michel', '+ 12'] as const;

@Component({
  selector: 'ds-hero',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="ds-hero" [class]="heroClasses()">
      @switch (layout()) {

        @case ('split') {
          <div class="ds-hero__inner">
            <div class="ds-hero__split-grid">
              <div class="ds-hero__content">
                @if (eyebrow()) {
                  <div class="ds-hero__eyebrow ds-hero__eyebrow--light">{{ eyebrow() }}</div>
                }
                <h1 class="ds-hero__title">{{ title() }}</h1>
                <p class="ds-hero__lead ds-hero__lead--light">{{ lead() }}</p>
                <div class="ds-hero__cta-row">
                  <button class="ds-hero__btn-primary">{{ primaryCta() }} <span>→</span></button>
                  <button class="ds-hero__btn-tertiary">{{ secondaryCta() }}</button>
                </div>
                @if (showProof()) {
                  <div class="ds-hero__proof ds-hero__proof--light">
                    <span class="ds-hero__proof-label">Ils utilisent DemandSens</span>
                    @for (logo of proofLogos; track logo; let i = $index) {
                      <span class="ds-hero__proof-logo" [class.ds-hero__proof-logo--muted]="i === 4">{{ logo }}</span>
                    }
                  </div>
                }
              </div>
              <div class="ds-hero__visual ds-hero__visual--split">
                <span class="ds-hero__visual-label">Capture produit · Tirer</span>
              </div>
            </div>
          </div>
        }

        @case ('centered') {
          <div class="ds-hero__inner ds-hero__inner--centered">
            @if (eyebrow()) {
              <div class="ds-hero__eyebrow ds-hero__eyebrow--light">{{ eyebrow() }}</div>
            }
            <h1 class="ds-hero__title ds-hero__title--centered">{{ title() }}</h1>
            <p class="ds-hero__lead ds-hero__lead--light ds-hero__lead--centered">{{ lead() }}</p>
            <div class="ds-hero__cta-row ds-hero__cta-row--centered">
              <button class="ds-hero__btn-primary">{{ primaryCta() }} <span>→</span></button>
              <button class="ds-hero__btn-tertiary">{{ secondaryCta() }}</button>
            </div>
            <div class="ds-hero__visual ds-hero__visual--centered">
              <span class="ds-hero__visual-label">Capture produit large · dashboard Core</span>
            </div>
          </div>
        }

        @case ('brand') {
          <div class="ds-hero__inner">
            <div class="ds-hero__brand-content">
              @if (eyebrow()) {
                <div class="ds-hero__eyebrow ds-hero__eyebrow--dark">{{ eyebrow() }}</div>
              }
              <h1 class="ds-hero__title">{{ title() }}</h1>
              <p class="ds-hero__lead ds-hero__lead--dark">{{ lead() }}</p>
              <div class="ds-hero__cta-row">
                <button class="ds-hero__btn-primary">{{ primaryCta() }} <span>→</span></button>
                <button class="ds-hero__btn-ghost-white">{{ secondaryCta() }}</button>
              </div>
            </div>
            @if (showProof()) {
              <div class="ds-hero__proof ds-hero__proof--dark">
                <span class="ds-hero__proof-label">Ils utilisent DemandSens</span>
                @for (logo of proofLogos; track logo; let i = $index) {
                  <span class="ds-hero__proof-logo" [class.ds-hero__proof-logo--muted]="i === 4">{{ logo }}</span>
                }
              </div>
            }
          </div>
        }

      }
    </section>
  `,
  styles: [`
    /* ── Base ─────────────────────────────────────────────────── */
    .ds-hero {
      font-family: var(--f-body);
      padding: 120px 0;

      @media (max-width: 768px) {
        padding: 64px 0;
      }
    }

    .ds-hero--split   { background: var(--surface); color: var(--on-surface); }
    .ds-hero--centered { background: var(--surface-secondary); color: var(--on-surface); }
    /* #400000 = brand DS BearingPoint — pas de token disponible */
    .ds-hero--brand   { background: #400000; color: #ffffff; }

    /* ── Inner container ──────────────────────────────────────── */
    .ds-hero__inner {
      max-width: 1280px;
      margin: 0 auto;
      padding: 0 40px;

      @media (max-width: 768px) {
        padding: 0 24px;
      }

      &--centered {
        text-align: center;
      }
    }

    /* ── Split grid ───────────────────────────────────────────── */
    .ds-hero__split-grid {
      display: grid;
      grid-template-columns: 1.1fr 1fr;
      gap: 64px;
      align-items: center;

      @media (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }

    /* ── Content ──────────────────────────────────────────────── */
    .ds-hero__brand-content {
      max-width: 880px;
    }

    /* ── Eyebrow ──────────────────────────────────────────────── */
    .ds-hero__eyebrow {
      font-family: var(--f-mono);
      font-size: 12px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 18px;

      &--light { color: #CC2831; }
      &--dark  { color: #FF6B6B; }
    }

    /* ── Title ────────────────────────────────────────────────── */
    .ds-hero__title {
      font-family: var(--f-display);
      font-size: clamp(44px, 6vw, 80px);
      font-weight: 700;
      line-height: 1.05;
      letter-spacing: -0.025em;
      margin: 0 0 24px;
      text-wrap: balance;

      &--centered {
        max-width: 980px;
        margin: 0 auto 24px;
      }
    }

    /* ── Lead ─────────────────────────────────────────────────── */
    .ds-hero__lead {
      font-size: 20px;
      line-height: 1.55;
      margin: 0 0 36px;
      max-width: 580px;
      text-wrap: pretty;

      &--light { color: var(--on-surface-muted); }
      &--dark  { color: rgba(255, 255, 255, 0.75); }

      &--centered {
        margin: 0 auto 36px;
      }
    }

    /* ── CTA row ──────────────────────────────────────────────── */
    .ds-hero__cta-row {
      display: flex;
      gap: 14px;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 36px;

      &--centered {
        justify-content: center;
      }
    }

    /* ── Buttons (scoped dans hero pour éviter dépendance circulaire) ── */
    .ds-hero__btn-primary {
      background: var(--c-accent);
      color: var(--c-accent-ink);
      border: none;
      padding: 16px 28px;
      border-radius: var(--r-sm);
      font-family: var(--f-body);
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      gap: 10px;
      align-items: center;
      min-height: 52px;
      transition:
        background-color var(--d-2) var(--e-standard),
        transform var(--d-2) var(--e-decel),
        box-shadow var(--d-2) var(--e-decel);

      &:hover {
        background-color: var(--c-accent-hover);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }

      &:focus-visible {
        outline: 2px solid var(--c-focus);
        outline-offset: 2px;
      }
    }

    .ds-hero__btn-tertiary {
      background: transparent;
      color: var(--on-surface);
      border: 1.5px solid var(--on-surface);
      padding: 16px 28px;
      border-radius: var(--r-sm);
      font-family: var(--f-body);
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      min-height: 52px;
      transition:
        background-color var(--d-2) var(--e-standard),
        transform var(--d-2) var(--e-decel),
        box-shadow var(--d-2) var(--e-decel);

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }

      &:focus-visible {
        outline: 2px solid var(--c-focus);
        outline-offset: 2px;
      }
    }

    .ds-hero__btn-ghost-white {
      background: transparent;
      color: #ffffff;
      border: 1.5px solid rgba(255, 255, 255, 0.4);
      padding: 16px 28px;
      border-radius: var(--r-sm);
      font-family: var(--f-body);
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      min-height: 52px;
      transition:
        background-color var(--d-2) var(--e-standard),
        transform var(--d-2) var(--e-decel),
        box-shadow var(--d-2) var(--e-decel);

      &:hover {
        background-color: rgba(255, 255, 255, 0.08);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }

      &:focus-visible {
        outline: 2px solid var(--c-focus);
        outline-offset: 2px;
      }
    }

    /* ── Visual placeholders ──────────────────────────────────── */
    .ds-hero__visual {
      background: repeating-linear-gradient(
        135deg,
        #F8EFEA,
        #F8EFEA 12px,
        #EFE3DC 12px,
        #EFE3DC 24px
      );
      border-radius: var(--r-md);
      border: 1px solid var(--border-subtle);
      display: flex;
      align-items: center;
      justify-content: center;

      &--split {
        aspect-ratio: 4 / 5;
      }

      &--centered {
        aspect-ratio: 16 / 9;
        max-width: 1100px;
        margin: 48px auto 0;
        box-shadow: var(--sh-5);
      }
    }

    .ds-hero__visual-label {
      font-family: var(--f-mono);
      font-size: 12px;
      color: #866B5A;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    /* ── Proof strip ──────────────────────────────────────────── */
    .ds-hero__proof {
      display: flex;
      gap: 28px;
      flex-wrap: wrap;
      align-items: center;
      padding-top: 24px;

      &--light { border-top: 1px dashed rgba(0, 0, 0, 0.12); }
      &--dark  { border-top: 1px dashed rgba(255, 255, 255, 0.18); }
    }

    .ds-hero__proof-label {
      font-family: var(--f-mono);
      font-size: 11px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--on-surface-muted);

      .ds-hero--brand & {
        color: rgba(255, 255, 255, 0.6);
      }
    }

    .ds-hero__proof-logo {
      font-family: var(--f-display);
      font-size: 14px;
      font-weight: 600;
      opacity: 0.85;

      &--muted { opacity: 0.6; }
    }
  `],
})
export class DsHeroComponent {
  readonly layout = input<DsHeroLayout>('split');
  readonly eyebrow = input<string | undefined>(undefined);
  readonly title = input<string>('');
  readonly lead = input<string>('');
  readonly primaryCta = input<string>('Demander une démo');
  readonly secondaryCta = input<string>('Voir le produit');
  readonly showProof = input<boolean>(true);

  readonly proofLogos = PROOF_LOGOS;

  readonly heroClasses = computed(() => `ds-hero--${this.layout()}`);
}
